import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    InventoryReportingChangesRequest,
    InventoryReportingChangesResponse,
} from '../../types/reporting';
import type { InventoryPartType } from '../../types/inventory';

const PAGE_SIZE_DEFAULT = 50;

const getInventoryReportingChanges = async (
    sessionId: string,
    input: InventoryReportingChangesRequest,
): Promise<InventoryReportingChangesResponse> => {
    const { data } = await axios.post<InventoryReportingChangesResponse>(
        `${API_URL}/reporting/auditing/inventory/changes`,
        input,
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export const useInventoryReportingChanges = (
    startDate: string,
    endDate: string,
    options?: {
        locationIds?: string[];
        preset?: string;
        types?: InventoryReportingChangesRequest['types'];
        tagIds?: string[];
        binIds?: string[];
        partTypes?: InventoryPartType[];
        supplierIds?: string[];
        search?: string;
        limit?: number;
        offset?: number;
        enabled?: boolean;
    },
) => {
    const sessionContext = useContext(SessionContext);
    const {
        locationIds,
        preset,
        types,
        tagIds,
        binIds,
        partTypes,
        supplierIds,
        search,
        limit = PAGE_SIZE_DEFAULT,
        offset = 0,
        enabled = true,
    } = options || {};

    return useQuery({
        queryKey: [
            'reporting',
            'auditing',
            'inventory',
            'changes',
            startDate,
            endDate,
            locationIds,
            preset,
            types,
            tagIds,
            binIds,
            partTypes,
            supplierIds,
            search,
            limit,
            offset,
        ],
        queryFn: () =>
            getInventoryReportingChanges(sessionContext?.sessionId ?? '', {
                startDate,
                endDate,
                locationIds,
                preset,
                types,
                tagIds,
                binIds,
                partTypes,
                supplierIds,
                search: search || undefined,
                limit,
                offset,
            }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });
};
